import { createContext, type PropsWithChildren, type ReactNode, Suspense, useContext } from 'react';
import {
  type CheckoutWebComponents,
  loadCheckoutWebComponents,
} from '@checkout.com/checkout-web-components';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useTheme } from '@repo/design-system-kit';
import { getEnvironmentVariables } from '@repo/shared-config/helper/environment-variables';
import { QontoErrorBoundary } from '#utils/error-boundary.tsx';
import { generateAppearance } from './checkout.config';
import {
  type PaymentSessionDetails,
  PaymentSessionProvider,
  usePaymentSession,
} from './payment-session';
import { CheckoutError } from '#checkout/checkout-error.tsx';

const ENV = getEnvironmentVariables();

const CheckoutContext = createContext<CheckoutWebComponents | null>(null);

export function useCheckout() {
  const value = useContext(CheckoutContext);

  if (!value) {
    throw new Error('useCheckout must be used inside a CheckoutProvider');
  }

  return value;
}

export interface CheckoutProviderProps extends PaymentSessionDetails {
  fallback?: ReactNode;
}

export function CheckoutProvider({
  fallback,
  paymentDetails,
  children,
}: PropsWithChildren<CheckoutProviderProps>) {
  return (
    <QontoErrorBoundary FallbackComponent={CheckoutError}>
      <Suspense fallback={fallback}>
        <PaymentSessionProvider paymentDetails={paymentDetails}>
          <InnerCheckoutProvider>{children}</InnerCheckoutProvider>
        </PaymentSessionProvider>
      </Suspense>
    </QontoErrorBoundary>
  );
}

function InnerCheckoutProvider({ children }: PropsWithChildren) {
  const { isDarkMode } = useTheme();
  const paymentSession = usePaymentSession();

  const { data: checkout } = useSuspenseQuery({
    queryKey: ['checkout-web-components', paymentSession, { isDarkMode }],
    queryFn: () => {
      return loadCheckoutWebComponents({
        appearance: generateAppearance(isDarkMode),
        publicKey: ENV.checkout.publicKey,
        paymentSession: {
          ...paymentSession,
          _links: paymentSession.links,
        },
      });
    },
  });

  return <CheckoutContext.Provider value={checkout}>{children}</CheckoutContext.Provider>;
}
